import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {DecimalPipe} from '@angular/common';
import {FormArray, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  FieldBlock, FormBlock, FormDefinition, FormPage, GroupBlock, supportsCondition
} from '../../../shared/models/form-schema.model';
import {FormFactoryService} from '../../../shared/services/form-factory.service';
import {FormPageComponent} from '../form-page/form-page.component';
import {applyServerFieldErrors, SubmissionFieldError} from '../../../shared/services/submission-errors';
import {controlAtPath, pathId} from '../../../shared/services/visibility-evaluator';
import {isDataImage} from '../../../shared/services/submission-display';
import {FormVisibilityState} from '../../services/form-visibility-state.service';

/** One row of the wizard's review summary. */
export interface SummaryField {
  label: string;
  value: any;
  /** The field's declared type; `list` for a repeating block's row count. Drives how the value is rendered. */
  type: string;
}

/** One field currently blocking the submit, as shown in the failed-submit summary (#470). */
export interface BlockingField {
  /** The field's human label (its key when it has none). */
  label: string;
  /** Index of the page the field lives on, or -1 when it could not be located. */
  pageIndex: number;
  /** That page's title, for the summary line. */
  pageTitle: string;
}

@Component({
  selector: 'forms-dynamic-form',
  standalone: true,
  imports: [DecimalPipe, ReactiveFormsModule, FormPageComponent],
  providers: [FormVisibilityState],
  templateUrl: './dynamic-form.component.html',
  styles: [`
    /* The host is a block-level form: default it to block so consumers' vertical margins / space-y apply
       (an inline host silently drops vertical margins). */
    :host {
      display: block;
    }

    :host ::ng-deep {
      .custom-apple-select .ng-select-container {
        @apply rounded-xl border border-gray-200 bg-white shadow-sm min-h-[42px] transition-all duration-200;
      }

      .custom-apple-select.ng-select-focused .ng-select-container {
        @apply border-theme-primary-500 ring-1 ring-theme-primary-500 shadow-md;
      }

      .custom-apple-select .ng-arrow-wrapper {
        @apply pr-3;
      }

      .custom-apple-select .ng-placeholder {
        @apply text-gray-300 font-normal;
      }

      .custom-apple-select .ng-value-label {
        @apply text-gray-900 font-medium sm:text-sm;
      }

      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }

      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    }
  `]
})
export class DynamicFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() definition!: FormDefinition;
  @Input() initialValue: any = {};
  /**
   * Whether to offer the "Entwurf speichern" (save-as-draft) affordance. Drafts are currently disabled across
   * submissions (to be implemented later), so this defaults to **false** and the button is hidden everywhere.
   * Re-enable by passing `[allowDraft]="true"` once the draft flow is brought back. The `saveDraft` output +
   * `onSaveDraft()` plumbing is kept intact (dormant) so re-enabling is just flipping this flag.
   */
  @Input() allowDraft = false;

  @Output() submitForm = new EventEmitter<any>();
  @Output() saveDraft = new EventEmitter<any>();
  /**
   * The form's raw value, emitted on every change and once the form has been (re)built (#503).
   *
   * Exists for a surface that renders part of the definition **outside** this wizard and must therefore
   * re-evaluate the conditional logic itself — today that is the attachments panel of the revision editor, which
   * strips `file` blocks out of the definition it hands over and has to know which of them the answers still ask
   * for. Such a surface cannot read {@link FormVisibilityState}: that verdict is computed from the *stripped*
   * definition and holds no entry for a block the wizard never received.
   *
   * So this deliberately emits the **value**, not a verdict. The value is the whole input to
   * `evaluateFormVisibility`, and the consumer already holds the full definition, so it can answer the question
   * for the blocks it owns without this component knowing they exist.
   *
   * Named `valueChanged` rather than `valueChange` on purpose: there is no matching `value` input, and
   * `valueChange` would invite a two-way `[(value)]` binding that cannot work.
   */
  @Output() valueChanged = new EventEmitter<any>();

  form!: FormGroup;
  currentPageIndex = 0;
  isReviewing = false;
  allBlocks: FormBlock[] = [];

  /** Whether the "why can't I submit" summary is on screen (set by a failed submit/review attempt, #470). */
  showValidationSummary = false;
  /** The fields currently blocking the submit — visible, enabled and invalid. Empty ⇒ nothing to list. */
  blockingFields: BlockingField[] = [];

  /** Guards {@link applyVisibility} against re-entering itself through a value change it caused. */
  private applying = false;
  private valueSub = new Subscription();

  constructor(private formFactory: FormFactoryService, private visibilityState: FormVisibilityState) {
  }

  get currentPage(): FormPage {
    return this.definition.pages[this.currentPageIndex];
  }

  get isLastPage(): boolean {
    return this.definition.pages.length === 0 || this.currentPageIndex === this.definition.pages.length - 1;
  }

  /** Cross-field validation messages currently failing (definition.validations). */
  get crossFieldErrors(): string[] {
    return (this.form?.errors?.['crossField'] as string[]) ?? [];
  }

  /**
   * Whether to render the failed-submit banner. It appears once a submit/review attempt failed, and either
   * names the blocking fields or — when nothing could be named and no cross-field rule explains it — falls back
   * to a generic sentence, so a failed attempt is never silently swallowed (#470).
   */
  get showBlockingBanner(): boolean {
    return this.showValidationSummary && (this.blockingFields.length > 0 || this.crossFieldErrors.length === 0);
  }

  ngOnInit() {
    this.buildForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['definition']) {
      if (!changes['definition'].firstChange) {
        this.buildForm();
      }
    }
  }

  ngOnDestroy() {
    this.valueSub.unsubscribe();
  }

  /**
   * The review summary for one page's blocks.
   *
   * Only fields that currently **apply** are listed: before #470 the summary walked every block regardless of
   * its `visibleWhen`, so a heavily conditional form ended on dozens of "–" rows for questions the employee was
   * never asked. A `signature` keeps its raw data URI as the value; the template renders it as an image rather
   * than printing the base64 blob.
   *
   * @param blocks the blocks to flatten (a page's blocks, or a nested group's)
   * @param parentPath the control path of the enclosing scope, as verbatim segments
   */
  getFlatFields(blocks: FormBlock[], parentPath: string[] = []): SummaryField[] {
    const fields: SummaryField[] = [];

    for (const block of blocks) {
      if (block.type === 'static-text') continue;

      const key = (block as { key: string }).key;
      const path = [...parentPath, key];
      // A field that does not apply is not "empty" — it was never asked. Leave it out entirely.
      if (!this.visibilityState.isBlockVisible(block, path)) continue;

      const control = controlAtPath(this.form, path);
      if (!control) continue;

      if (block.type === 'group') {
        fields.push(...this.getFlatFields((block as GroupBlock).blocks, path));
      } else if (block.type === 'list') {
        /*
         * A COUNT, deliberately, and deliberately not the row-by-row rendering the two detail views gained in
         * #499. The three surfaces have different jobs: a detail view is a stored record someone reads to find
         * out what was answered, while this is a pre-submit confirmation of what the employee has just typed
         * and can still see on the previous page. Expanding an unbounded list here would swamp a two-column
         * summary whose value is being scannable. It also reads from the live `FormArray`, so it has none of
         * the row-identity problem #498 solved for stored groups.
         */
        fields.push({
          label: block.label || key,
          value: `${(control as FormArray).length} Einträge`,
          type: 'list'
        });
      } else {
        const field = block as FieldBlock;
        let displayValue = control.value;

        if (field.type === 'select' && field.options) {
          const option = field.options.find(o => o.value === control.value);
          displayValue = option ? option.label : control.value;
        } else if (field.type === 'boolean') {
          displayValue = control.value ? 'Ja' : 'Nein';
        } else if (field.type === 'date' && control.value) {
          displayValue = new Date(control.value).toLocaleDateString('de-DE');
        }

        fields.push({label: block.label || key, value: displayValue, type: field.type});
      }
    }

    return fields;
  }

  /** Whether a summary row should render as a drawn signature image rather than as text (#470). */
  isSignatureImage(field: SummaryField): boolean {
    return field.type === 'signature' && isDataImage(field.value);
  }

  nextPage() {
    if (this.isLastPage) {
      if (this.form.valid) {
        this.clearValidationSummary();
        this.isReviewing = true;
      } else {
        this.reportInvalid();
      }
    } else {
      this.currentPageIndex++;
    }
  }

  prevPage() {
    if (this.isReviewing) {
      this.isReviewing = false;
    } else if (this.currentPageIndex > 0) {
      this.currentPageIndex--;
    }
  }

  goToPage(index: number) {
    this.isReviewing = false;
    this.currentPageIndex = index;
  }

  onSubmit() {
    if (this.form.valid) {
      this.clearValidationSummary();
      this.submitForm.emit(this.form.getRawValue());
    } else {
      this.reportInvalid();
    }
  }

  onSaveDraft() {
    this.saveDraft.emit(this.form.getRawValue());
  }

  /**
   * Applies a server `422` to the live form: each reported field is marked and shows its own message, and the
   * wizard leaves the review step for the first page that actually holds a rejected field — so the employee lands
   * on the problem instead of on a generic banner with nothing highlighted (#258).
   *
   * @returns the failures that matched no control (form-level / unknown key), for the caller's banner.
   */
  applyServerErrors(errors: SubmissionFieldError[]): SubmissionFieldError[] {
    if (!this.form || errors.length === 0) return errors;

    const unmatched = applyServerFieldErrors(this.form, errors);

    const matched = errors.filter(e => !unmatched.includes(e)).map(e => e.fieldKey);
    const page = this.definition.pages.findIndex(p => matched.some(key => pageHasField(p.blocks, key)));
    if (page >= 0) {
      this.isReviewing = false;
      this.currentPageIndex = page;
    } else if (matched.length > 0) {
      this.isReviewing = false; // rejected field exists but sits outside the page walk — still leave the summary
    }

    return unmatched;
  }

  /**
   * Reaction to a failed submit / "Prüfen & Absenden": say what is blocking and go there.
   *
   * `markAllAsTouched()` alone was the whole feedback before #470, and it shows nothing at all when the
   * offending control sits on another page — which, in a multi-page conditional form, is the normal case. The
   * summary is therefore computed from the whole-definition verdict, and the wizard jumps to the first page that
   * actually holds a blocking field.
   */
  private reportInvalid(): void {
    this.form.markAllAsTouched();
    this.isReviewing = false;
    this.blockingFields = this.collectBlockingFields();
    this.showValidationSummary = true;

    const first = this.blockingFields.find(field => field.pageIndex >= 0);
    if (first) this.currentPageIndex = first.pageIndex;
  }

  /** Drop the failed-submit banner (the attempt succeeded, or nothing is blocking any more). */
  private clearValidationSummary(): void {
    this.showValidationSummary = false;
    this.blockingFields = [];
  }

  /**
   * The fields that currently keep the form from submitting: **visible**, enabled and invalid.
   *
   * A hidden field can never appear here. It is disabled by {@link applyVisibility} and so would be filtered out
   * anyway, but the `visible` check is kept as the primary filter because "does not apply" is the fact the
   * summary is about — telling an employee to fill in a question the form never asked is worse than no summary.
   */
  private collectBlockingFields(): BlockingField[] {
    const blocking: BlockingField[] = [];
    const seen = new Set<string>();

    for (const field of this.visibilityState.verdict().fields) {
      if (!field.visible) continue;

      const control = controlAtPath(this.form, field.path);
      if (!control || control.disabled || control.valid) continue;

      const id = pathId(field.path);
      if (seen.has(id)) continue;
      seen.add(id);

      const pageIndex = this.definition.pages.findIndex(page => pageHasField(page.blocks, field.key));
      blocking.push({
        label: field.block.label || field.key,
        pageIndex,
        pageTitle: pageIndex >= 0 ? this.definition.pages[pageIndex].title : ''
      });
    }

    return blocking;
  }

  /**
   * Evaluates the conditional logic for the **whole definition** and applies its consequences to every control —
   * not only the ones currently rendered.
   *
   * This is the core of #470. Visibility used to be applied by each rendered `BlockRendererComponent`, so
   * leaving a page destroyed the only thing that would ever have neutralised its now-hidden required fields:
   * they stayed enabled and empty, `form.valid` stayed false forever, and nothing on screen said why.
   *
   * The two conditions stay distinct: `visibleWhen` false ⇒ the field does not apply (disabled, and omitted from
   * the submission); `enabledWhen` false ⇒ it applies but is read-only (disabled, still submitted).
   *
   * Containers are applied before leaves on purpose: `FormGroup.enable()` cascades to every descendant, so a
   * visible group would otherwise re-enable the hidden fields inside it. Everything runs with
   * `{emitEvent: false}` — `enable`/`disable` emit `valueChanges` by default, which would re-enter this method
   * from its own subscription.
   */
  private applyVisibility(): void {
    if (!this.form || !this.definition || this.applying) return;

    this.applying = true;
    try {
      const verdict = this.visibilityState.update(this.definition, this.form.getRawValue());

      for (const container of verdict.containers) {
        const control = controlAtPath(this.form, container.path);
        if (!control) continue;
        // Two independent reasons to disable a container: it does not apply at all (hidden — kept out of
        // validation), or it applies but is read-only (`enabledWhen`, #492 — still rendered). The verdict has
        // already folded any enclosing container's state into `enabled`, so this needs no ordering trick.
        // A `requiredWhen` on a repeating list means "at least one entry" — `Validators.required` on a
        // `FormArray` rejects an empty array (Angular's `isEmptyInputValue` → `lengthOrSize` → `value.length`).
        // Nothing else in the codebase enforces that: `minItems` seeds rows and gates the remove button, it
        // never validates (#493).
        if (supportsCondition(container.block.type, 'requiredWhen')) {
          const hasRequired = control.hasValidator(Validators.required);
          if (container.required && !hasRequired) {
            control.addValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          } else if (!container.required && hasRequired) {
            control.removeValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          }
        }

        const shouldBeEnabled = container.visible && container.enabled;
        if (shouldBeEnabled && control.disabled) control.enable({emitEvent: false});
        else if (!shouldBeEnabled && control.enabled) control.disable({emitEvent: false});
      }

      for (const field of verdict.fields) {
        const control = controlAtPath(this.form, field.path);
        if (!control) continue;

        // Conditionally-required fields: toggle only the required validator, leaving the field's own validators
        // untouched. A statically-required field is never un-required (the verdict already folds that in).
        if (field.block.requiredWhen) {
          const hasRequired = control.hasValidator(Validators.required);
          if (field.required && !hasRequired) {
            control.addValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          } else if (!field.required && hasRequired) {
            control.removeValidators(Validators.required);
            control.updateValueAndValidity({emitEvent: false});
          }
        }

        const shouldBeEnabled = field.visible && field.enabled;
        if (shouldBeEnabled && control.disabled) control.enable({emitEvent: false});
        else if (!shouldBeEnabled && control.enabled) control.disable({emitEvent: false});
      }

      this.form.updateValueAndValidity({emitEvent: false});
    } finally {
      this.applying = false;
    }

    // Keep an open failure summary honest: it must shrink as the employee fixes things, and disappear when
    // nothing is blocking any more.
    if (this.showValidationSummary) {
      this.blockingFields = this.collectBlockingFields();
      if (this.blockingFields.length === 0 && this.crossFieldErrors.length === 0) this.clearValidationSummary();
    }
  }

  /**
   * (Re)builds the reactive form from {@link definition} and restores whatever data is currently in play.
   *
   * **Precedence (#388), and it matters:** the factory now seeds each control with the field's declared
   * *default*, so restoring data can no longer be a blanket `patchValue` of a whole snapshot.
   *
   * - An existing submission's stored value **always wins**, including a stored *empty* one. An employee who
   *   deliberately left a field blank must not find it silently repopulated on the next open.
   * - A default therefore only survives where the incoming data carries **no key at all** for that control.
   *   Absent ≠ present-but-empty — which is exactly why {@link applyPresentValues} walks the tree and patches
   *   key by key instead of handing the object to `FormGroup.patchValue` (that treats a missing key and an
   *   `undefined` value alike, and would have wiped defaults on a first render).
   *
   * The same rule serves both sources without a special case:
   * - {@link initialValue} (a restored submission) only carries the keys the submission actually stored, so
   *   untouched/new fields keep their defaults.
   * - `this.form.getRawValue()` (the definition changed while the user was mid-edit) carries *every* existing
   *   key, so everything the user typed — and everything the user cleared — is preserved verbatim, while only
   *   controls newly introduced by the changed definition start from their default.
   */
  private buildForm() {
    if (!this.definition) {
      return;
    }

    // Capture current values before we destroy the old form
    const currentValues = this.form ? this.form.getRawValue() : (this.initialValue || {});

    this.form = this.formFactory.buildFormFromDefinition(this.definition);

    if (currentValues && Object.keys(currentValues).length > 0) {
      // Repeating rows first: a patch only writes into controls that already exist, so a FormArray that is
      // still empty would silently swallow every row (#259). Grow it to the incoming row count, then patch.
      this.formFactory.syncListRows(this.form, this.definition, currentValues);
      applyPresentValues(this.form, currentValues, false);
    }

    // The form owns the conditional logic for the whole definition, so this subscription — not the rendered
    // block renderers — is what keeps hidden fields neutralised. It is registered before the renderers' own
    // subscriptions (they subscribe from a setTimeout), so they always read a current verdict.
    this.valueSub.unsubscribe();
    this.valueSub = new Subscription();
    // `applyVisibility` first, then publish: a consumer of `valueChanged` re-evaluates the same rules against the
    // value it is handed, so it must never see a value the form itself has not finished reacting to.
    this.valueSub.add(this.form.valueChanges.subscribe(() => {
      this.applyVisibility();
      this.valueChanged.emit(this.form.getRawValue());
    }));
    this.applyVisibility();
    // The initial state matters as much as the changes: a form opened on a stored answer that already hides a
    // block must render that way on first paint, not only after the employee touches something.
    this.valueChanged.emit(this.form.getRawValue());

    // Explicitly notify root form to trigger rule evaluation in all block-renderers
    // We use a small timeout to ensure all components are initialized and listening
    setTimeout(() => {
      // Re-apply values ONE MORE TIME after components have settled,
      // and this time with emitEvent: true to trigger rules
      if (currentValues && Object.keys(currentValues).length > 0) {
        this.formFactory.syncListRows(this.form, this.definition, currentValues);
        applyPresentValues(this.form, currentValues, true);
      }
      this.applyVisibility();
      this.form.updateValueAndValidity({emitEvent: true});
    }, 100); // Slightly longer delay to ensure full DOM/Component lifecycle completion

    this.extractAllBlocks();

    // Safety check for page index after schema changes
    if (this.currentPageIndex >= this.definition.pages.length) {
      this.currentPageIndex = Math.max(0, this.definition.pages.length - 1);
    }
  }

  private extractAllBlocks() {
    this.allBlocks = [];
    this.definition.pages.forEach(page => {
      this.allBlocks.push(...page.blocks);
    });
  }
}

/**
 * Writes `value` into `group`, **but only for the keys `value` actually carries** — every other control keeps
 * whatever it was built with (its #388 default, or the type's empty value).
 *
 * This is the deliberate replacement for `FormGroup.patchValue(value)`: `patchValue` cannot express "this key
 * was not supplied", so a snapshot that legitimately holds `''` for a field the employee left blank and a
 * snapshot that simply never mentioned that field are indistinguishable to it. Getting that wrong in either
 * direction is a data bug — a default overwriting a real (empty) answer, or a stale default resurfacing.
 *
 * Nested `group` blocks recurse. `list` rows are matched by index against the incoming array; rows the incoming
 * data does not reach (e.g. extra `minItems` rows) keep their defaults, which is the correct reading of "no data
 * for this row". {@link FormFactoryService.syncListRows} must have run first — this never creates controls.
 *
 * @param group the form (or sub-group / row) to write into
 * @param value the incoming data for exactly that group
 * @param emitEvent passed through to each `setValue`, preserving the caller's existing event semantics
 */
function applyPresentValues(group: FormGroup, value: any, emitEvent: boolean): void {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return;

  for (const key of Object.keys(group.controls)) {
    // `hasOwnProperty`, not a truthiness/undefined check: `{answer: ''}` IS an answer, `{}` is not.
    if (!Object.prototype.hasOwnProperty.call(value, key)) continue;

    const control = group.controls[key];
    const incoming = value[key];

    if (control instanceof FormGroup) {
      applyPresentValues(control, incoming, emitEvent);
    } else if (control instanceof FormArray) {
      if (!Array.isArray(incoming)) continue;
      control.controls.forEach((row, index) => {
        if (index >= incoming.length) return; // no incoming data for this row — leave its defaults alone
        if (row instanceof FormGroup) applyPresentValues(row, incoming[index], emitEvent);
        else row.setValue(incoming[index], {emitEvent});
      });
    } else {
      control.setValue(incoming, {emitEvent});
    }
  }
}

/** Whether a page's block tree contains a field with this (globally unique) leaf key. */
function pageHasField(blocks: FormBlock[], fieldKey: string): boolean {
  return blocks.some(block => {
    if (block.type === 'group' || block.type === 'list') {
      return pageHasField((block as GroupBlock).blocks, fieldKey);
    }
    return block.type !== 'static-text' && (block as FieldBlock).key === fieldKey;
  });
}
